/* eslint-disable max-len */
import React, { useEffect, useContext } from 'react';
import { Route, Routes, useLocation } from 'react-router-dom';
import { batchActions, hideSlideOutPanel, modeUIContext, setIsEditingForm, setUsageMode, UsageMode } from '@moderepo/mode-ui-state';
import { SubRoute, RouteGroup } from '@moderepo/mode-app-components';
import { ProjectsListingPage, ActivateAgent, Login, ForgotPassword, NotFound, MyAccountPage, ResetPassword, NotAuthorized } from '..';
import { RitzRoute } from './RitzRoute';
import { VerifyAgent } from '../auth/VerifyAgent';
import { ProjectRoutesSwitch } from './ProjectRoutesSwitch';
import { DEFAULT_SUB_ROUTE_NAME } from '../../utils/ritzExtensionUtils';
import { ExternalLoginCallback } from '../auth/ExternalLoginCallback';

/**
 * The route prefixes to use for all routes that are under manage or view modes. For example, if a route is to be used for MANAGE mode,
 * its path will start with 'manage'. If a route is to be used for VIEW mode, its path needs to start with 'view'.
 *
 * Routes used for manage mode. When in these routes, the user will have access to make changes to the objects, Users, Homes, Devices, Entities, etc..
 *      /projects/1234/manage/users/567
 *      /projects/1234/manage/entities
 *
 * Routes used for view mode. When in these routes, the user can only view the data but not make any changes.
 *      /projects/1234/view/users/789
 *      /projects/1234/view/homes
 *      /projects/1234/view/entities/my_entity
 *
 */
export const MANAGE_DATA_ROUTES_PREFIX = 'manage';
export const VIEW_DATA_ROUTES_PREFIX = 'view';



/**
 * Get the route path config for device details page. Because there are 2 paths that show device details
 *      - /projects/12/devices/78                       // without user/home context
 *      - /projects/12/users/34/home/56/devices/78      // with user/home context
 * The sub route settings for all of those paths are the same so instead of copy and paste this config, we will make a function that return
 * the config can call it when we need it. This way if we need to change the config, we only need to change 1 place
 *
 * @param rootPath - The path of the route.
 */
const getDeviceDetailsPageRouteGroup = (rootPath: string): RouteGroup => {
    return {
        rootPath,
        subRoutes: [
            {
                name        : DEFAULT_SUB_ROUTE_NAME.SETTINGS,
                relativePath: '',
                label       : 'label.settings',
            } as SubRoute,
            {
                name        : DEFAULT_SUB_ROUTE_NAME.LOGS,
                relativePath: '/logs',
                label       : 'label.logs',
            } as SubRoute,
        ],
    };
};


/**
 * Get the route path config for user details page. Because there can be multiple paths that show user details therefore we will make a function
 * that return the config can call it when we need it. This way if we need to change the config, we only need to change 1 place
 *
 * @param rootPath - The path of the route.
 */
const getUserDetailsPageRouteGroup = (rootPath: string): RouteGroup => {
    return {
        rootPath,
        subRoutes: [
            {
                name        : DEFAULT_SUB_ROUTE_NAME.DEFAULT,
                relativePath: '',
                label       : 'label.user_info',
            } as SubRoute,
            {
                name        : DEFAULT_SUB_ROUTE_NAME.LOGS,
                relativePath: '/logs',
                label       : 'label.logs',
            } as SubRoute,
        ],
    };
};


/**
 * Get the route path config for home details page. Because there can be multiple paths that show home details therefore we will make a function
 * that return the config can call it when we need it. This way if we need to change the config, we only need to change 1 place
 *
 * @param rootPath - The path of the route.
 */
const getHomeDetailsPageRouteGroup = (rootPath: string): RouteGroup => {
    return {
        rootPath,
        subRoutes: [
            {
                name        : DEFAULT_SUB_ROUTE_NAME.DEFAULT,
                relativePath: '',
                label       : 'label.overview',
            } as SubRoute,
            {
                name        : DEFAULT_SUB_ROUTE_NAME.HOME_MEMBERS,
                relativePath: '/home-members',
                label       : 'terms.member_other',
            } as SubRoute,
            {
                name        : DEFAULT_SUB_ROUTE_NAME.DEVICES,
                relativePath: '/devices',
                label       : 'terms.device_other',
            } as SubRoute,
            {
                name        : DEFAULT_SUB_ROUTE_NAME.TSDB,
                relativePath: '/tsdb',
                label       : 'label.tsdb',
            } as SubRoute,
            {
                name        : DEFAULT_SUB_ROUTE_NAME.VIDEOS,
                relativePath: '/videos',
                label       : 'label.video_other',
            } as SubRoute,
            {
                name        : DEFAULT_SUB_ROUTE_NAME.SETTINGS,
                relativePath: '/settings',
                label       : 'label.settings',
            } as SubRoute,
            {
                name        : DEFAULT_SUB_ROUTE_NAME.LOGS,
                relativePath: '/logs',
                label       : 'label.logs',
            } as SubRoute,
        ],
    };
};



const getAdminPageRouteGroup = (rootPath: string): RouteGroup => {
    return {
        rootPath,
        subRoutes: [
            {
                name        : DEFAULT_SUB_ROUTE_NAME.DEFAULT,
                relativePath: '',
                label       : 'label.project_settings',
            } as SubRoute,
            {
                name        : DEFAULT_SUB_ROUTE_NAME.AGENTS,
                relativePath: '/agents',
                label       : 'label.agents',
            } as SubRoute,
            {
                name        : DEFAULT_SUB_ROUTE_NAME.AUDIT_LOGS,
                relativePath: '/audit-logs',
                label       : 'label.audit_logs',
            } as SubRoute,
        ],
    };
};



/**
 * This defines all the routes the Ritz app has. It is just an Object of Route Name => Route Path template. Because some of the routes are kind of
 * related, we group them together so that we can display them as Tabs. Therefore, some of these routes are not Route Name => Route Path template
 * but instead they will be Route Name => Route Group and the route group will contain a set of routes belonged to that group.
 *
 * NOTE: that this only define the paths for the BASIC RITZ app, NOT including project specific route paths.
 */
export const DEFAULT_ROUTE_PATHS = {
    HOME                                                             : '/',
    ACTIVATE                                                         : '/activate',
    VERIFY                                                           : '/verify',
    LOGIN                                                            : '/login',
    EXTERNAL_AUTH_LOGIN_CALLBACK                                     : '/oidc/:providerId',
    FORGOT_PASSWORD                                                  : '/forgot-password',
    RESET_PASSWORD                                                   : '/reset-password',
    MY_ACCOUNT                                                       : '/my-account',
    NOT_AUTHORIZED                                                   : '/not-authorized',
    PROJECTS_$projectId                                              : '/projects/:projectId',
    PROJECTS_$projectId_MY_ACCOUNT                                   : '/projects/:projectId/my-account',
    PROJECTS_$projectId_ADMIN                                        : getAdminPageRouteGroup('/projects/:projectId/admin'),
    PROJECTS_$projectId_HOMES_$homeId                                : getHomeDetailsPageRouteGroup('/projects/:projectId/homes/:homeId'),
    PROJECTS_$projectId_USERS_$userId_HOMES_$homeId                  : getHomeDetailsPageRouteGroup('/projects/:projectId/users/:userId/homes/:homeId'),
    PROJECTS_$projectId_USERS_$userId                                : getUserDetailsPageRouteGroup('/projects/:projectId/users/:userId'),
    PROJECTS_$projectId_DEVICES_$deviceId                            : getDeviceDetailsPageRouteGroup('/projects/:projectId/devices/:deviceId'),
    PROJECTS_$projectId_HOMES_$homeId_DEVICES_$deviceId              : getDeviceDetailsPageRouteGroup('/projects/:projectId/homes/:homeId/devices/:deviceId'),
    PROJECTS_$projectId_USERS_$userId_HOMES_$homeId_DEVICES_$deviceId: getDeviceDetailsPageRouteGroup('/projects/:projectId/users/:userId/homes/:homeId/devices/:deviceId'),
    PROJECTS_$projectId_HOMES_$homeId_TSDB_$dataId                   : '/projects/:projectId/homes/:homeId/tsdb/:dataId',
    PROJECTS_$projectId_USERS_$userId_HOMES_$homeId_TSDB_$dataId     : '/projects/:projectId/users/:userId/homes/:homeId/tsdb/:dataId',
    PROJECTS_$projectId_HOMES                                        : '/projects/:projectId/homes',
    PROJECTS_$projectId_USERS                                        : '/projects/:projectId/users',
    PROJECTS_$projectId_DEVICES                                      : '/projects/:projectId/devices',
    PROJECTS_$projectId_DOWNLOADS                                    : '/projects/:projectId/downloads',
    PROJECTS_$projectId_MANAGE_ENTITIES                              : `/projects/:projectId/${MANAGE_DATA_ROUTES_PREFIX}/entities`,
    PROJECTS_$projectId_MANAGE_ENTITIES_$entityId                    : `/projects/:projectId/${MANAGE_DATA_ROUTES_PREFIX}/entities/:entityId`,
    PROJECTS_$projectId_VIEW_ENTITIES                                : `/projects/:projectId/${VIEW_DATA_ROUTES_PREFIX}/entities`,
    PROJECTS_$projectId_VIEW_ENTITIES_$entityId                      : `/projects/:projectId/${VIEW_DATA_ROUTES_PREFIX}/entities/:entityId`,
    PROJECTS_$projectId_ALERT_RULES                                  : '/projects/:projectId/alert-rules',
    PROJECTS_$projectId_ALERTS                                       : '/projects/:projectId/alerts',
    PROJECTS_$projectId_ALERTS_$alertId                              : '/projects/:projectId/alerts/:alertId',
    PROJECTS_$projectId_NOT_AUTHORIZED                               : '/projects/:projectId/not-authorized',

    // HIDDEN routes
    PROJECTS_$projectId_ADMIN_UI_SETTINGS            : '/projects/:projectId/admin/uiSettings',
    PROJECTS_$projectId_ADMIN_DASHBOARD_SETTINGS     : '/projects/:projectId/admin/dashboardSettings',
    PROJECTS_$projectId_ADMIN_ENTITY_CLASSES_SETTINGS: '/projects/:projectId/admin/entityClassesSettings',
    PROJECTS_$projectId_ADMIN_TRANSLATIONS_SETTINGS  : '/projects/:projectId/admin/translationsSettings',
};


/**
 * Given the pathname e.g.
 *      "/projects/1234/view/entities/abc"
 *      or "/projects/1234/manage/entities/abc"
 *      or "/projects/1234/homes/123"
 *      or "/my-account" etc...
 *
 * Check if the path has this pattern "/projects/PROJECT_ID/USAGE_MODE/" where USAGE_MODE is either "view" or "manage" and return the usage mode.
 * If there is no "view" or "manage" in the path then return the default usage mode, MANAGE_DATA
 */
const getUsageModeFromRoutePathname = (pathname: string): UsageMode => {
    // break the pathname into tokens e.g. from "projects/1234/view/entities/..." to ["projects", "1234", "view", "entities", ...]
    const pathnameTokens = pathname.substring(1).split('/');

    // We know all project routes will start with "/projects/:projectId/..." so we just use one of them and break it into tokens
    // like this ["projects", ":projectId", "view", ...]
    const projectRouteTokens = DEFAULT_ROUTE_PATHS.PROJECTS_$projectId.substring(1).split('/');

    // if the first token from pathnameTokens and projectRouteTokens matches then we know the pathname is a path that starts with
    // "/projects/" therefore we can now check the 3rd token from pathnameTokens to see if it is "view" or "manage".
    if (pathnameTokens.length > 0 && projectRouteTokens.length > 0 && pathnameTokens[0] === projectRouteTokens[0]) {
        const thirdToken = pathnameTokens[2];
        if (thirdToken !== undefined && thirdToken === VIEW_DATA_ROUTES_PREFIX) {
            return UsageMode.VIEW_DATA;
        }
    }

    return UsageMode.MANAGE_DATA;
};


export const AppRoutes: React.FC = () => {
    const { dispatch:uiDispatch } = useContext(modeUIContext);
    const location = useLocation();

    useEffect(() => {
        // Route changed, need to update some UI states.
        const usageMode = getUsageModeFromRoutePathname(location.pathname);
        uiDispatch(batchActions([
            setIsEditingForm(false),
            hideSlideOutPanel(),
            setUsageMode(usageMode),
        ]));
    }, [location, uiDispatch]);


    return (
        <Routes>
            {/* Routes for NOT logged in agents e.g. login, reset password, verify, etc... */}
            <Route
                path={DEFAULT_ROUTE_PATHS.ACTIVATE}
                element={(
                    <RitzRoute
                        requiredLogin={false}
                        element={<ActivateAgent />}
                    />
                )}
            />
            <Route
                path={DEFAULT_ROUTE_PATHS.VERIFY}
                element={(
                    <RitzRoute
                        requiredLogin={false}
                        element={<VerifyAgent />}
                    />
                )}
            />

            <Route
                path={DEFAULT_ROUTE_PATHS.LOGIN}
                element={(
                    <RitzRoute
                        requiredLogin={false}
                        element={<Login />}
                    />
                )}
            />
            <Route
                path={DEFAULT_ROUTE_PATHS.EXTERNAL_AUTH_LOGIN_CALLBACK}
                element={(
                    <RitzRoute
                        requiredLogin={false}
                        element={<ExternalLoginCallback />}
                    />
                )}
            />
            <Route
                path={DEFAULT_ROUTE_PATHS.FORGOT_PASSWORD}
                element={(
                    <RitzRoute
                        requiredLogin={false}
                        element={<ForgotPassword />}
                    />
                )}
            />
            <Route
                path={DEFAULT_ROUTE_PATHS.RESET_PASSWORD}
                element={(
                    <RitzRoute
                        requiredLogin={false}
                        element={<ResetPassword />}
                    />
                )}
            />

            {/* Routes for LOGGED IN in agents. Agents needs to be logged in before entering these routes */}
            <Route
                path={DEFAULT_ROUTE_PATHS.MY_ACCOUNT}
                element={(
                    <RitzRoute
                        requiredLogin
                        element={<MyAccountPage />}
                    />
                )}
            />
            <Route
                path={DEFAULT_ROUTE_PATHS.HOME}
                element={(
                    <RitzRoute
                        requiredLogin
                        element={<ProjectsListingPage />}
                    />
                )}
            />
            <Route
                path={DEFAULT_ROUTE_PATHS.NOT_AUTHORIZED}
                element={<NotAuthorized />}
            />

            {/* Routes for PROJECT related pages. All pages that has project context will go through here first. */}
            <Route
                path={`${DEFAULT_ROUTE_PATHS.PROJECTS_$projectId}/*`}
                element={
                    <ProjectRoutesSwitch />
                }
            />

            <Route
                path="*"
                element={<NotFound />}
            />
        </Routes>
    );
};
